import { useEffect, useRef, useState } from 'react'
import { supabase } from '../lib/supabase.js'
import { useAuth } from '../hooks/useAuth.jsx'
import { AUDIO_TYPES, IMAGE_TYPES, MAX_BYTES, MAX_FILES, MEDIA_BUCKET, validateMedia } from '../lib/assetMedia.js'

function DraftPreview({ file }) {
  const [url, setUrl] = useState('')
  useEffect(() => {
    if (!URL.createObjectURL) return
    const next = URL.createObjectURL(file); setUrl(next)
    return () => URL.revokeObjectURL(next)
  }, [file])
  if (!url) return null
  return file.type.startsWith('image/')
    ? <img src={url} alt={`Preview ${file.name}`} className="mt-1 max-h-32 max-w-full rounded border border-border object-contain"/>
    : <audio src={url} controls preload="metadata" aria-label={`Preview ${file.name}`} className="mt-1 max-w-full"/>
}

export function MediaGallery({ attachments = [] }) {
  const owner = useAuth()?.user?.id
  const [state, setState] = useState({ key: '', items: [], error: '' })
  const key = `${owner}:${JSON.stringify(attachments)}`
  useEffect(() => {
    let active = true
    async function load() {
      try {
        const items = await Promise.all(attachments.map(async item => {
          if (!owner || !item.path?.startsWith(`${owner}/`) || ![...IMAGE_TYPES, ...AUDIO_TYPES].includes(item.type)) throw new Error('Attachment is unavailable.')
          const { data, error } = await supabase.storage.from(MEDIA_BUCKET).createSignedUrl(item.path, 3600)
          if (error) throw error
          return { ...item, url: data.signedUrl }
        }))
        if (active) setState({ key, items, error: '' })
      } catch { if (active) setState({ key, items: [], error: 'Attachments could not be loaded. Reopen to retry.' }) }
    }
    if (attachments.length) load()
    const timer = attachments.length ? setInterval(load, 50 * 60 * 1000) : null
    return () => { active = false; clearInterval(timer) }
  }, [key])
  if (!attachments.length) return null
  const current = state.key === key ? state : { items: [], error: '' }
  return <div className="space-y-2 mt-2">
    {current.error && <p role="alert" className="text-xs text-red-500">{current.error}</p>}
    {!current.error && !current.items.length && <p role="status" className="text-xs text-inkMuted">Loading attachments…</p>}
    {current.items.map(item => <div key={item.path}>
      {item.type.startsWith('image/') ? <a href={item.url} target="_blank" rel="noopener noreferrer" aria-label={`Open ${item.name}`}><img src={item.url} alt={item.name} className="max-h-80 max-w-full rounded border border-border object-contain"/></a> : <audio controls preload="metadata" src={item.url} aria-label={item.name} className="max-w-full"/>}
    </div>)}
  </div>
}

export function MediaPicker({ files, onChange, imagesOnly = false, disabled = false, existingCount = 0, onRecordingChange }) {
  const [error, setError] = useState(''), [recording, setRecording] = useState(false), [requesting, setRequesting] = useState(false)
  const recorder = useRef(null), stream = useRef(null), timer = useRef(null), alive = useRef(true), cancelled = useRef(false)
  const latest = useRef({ files, onChange, existingCount, onRecordingChange }); latest.current = { files, onChange, existingCount, onRecordingChange }
  function stopTracks() { stream.current?.getTracks().forEach(track => track.stop()); stream.current = null; clearTimeout(timer.current) }
  useEffect(() => { alive.current = true; return () => { alive.current = false; cancelled.current = true; if (recorder.current?.state === 'recording') recorder.current.stop(); stopTracks(); latest.current.onRecordingChange?.(false) } }, [])
  function add(incoming) {
    try {
      if (latest.current.files.length + latest.current.existingCount + incoming.length > MAX_FILES) throw new Error('Attach up to five files.')
      incoming.forEach(file => validateMedia(file, imagesOnly))
      latest.current.onChange([...latest.current.files, ...incoming]); setError('')
    } catch (failure) { setError(failure.message) }
  }
  async function record() {
    setError(''); setRequesting(true); latest.current.onRecordingChange?.(true); cancelled.current = false
    try {
      if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') throw new Error('Voice recording is unavailable in this browser. Attach an audio file instead.')
      const media = await navigator.mediaDevices.getUserMedia({ audio: true })
      if (!alive.current) { media.getTracks().forEach(track => track.stop()); return }
      stream.current = media
      const mimeType = ['audio/webm;codecs=opus', 'audio/mp4', 'audio/ogg;codecs=opus'].find(type => MediaRecorder.isTypeSupported(type))
      const instance = new MediaRecorder(media, mimeType ? { mimeType, audioBitsPerSecond: 64000 } : undefined)
      recorder.current = instance
      const chunks = []; let size = 0
      instance.ondataavailable = event => { if (event.data.size) { chunks.push(event.data); size += event.data.size; if (size >= MAX_BYTES && instance.state === 'recording') instance.stop() } }
      instance.onstop = () => {
        stopTracks()
        if (!alive.current) return
        setRecording(false); latest.current.onRecordingChange?.(false)
        if (!cancelled.current) add([new File(chunks, 'Voice note', { type: instance.mimeType || mimeType || 'audio/webm' })])
      }
      instance.onerror = () => { cancelled.current = true; stopTracks(); if (alive.current) { setError('Recording failed. Please try again.'); setRecording(false); latest.current.onRecordingChange?.(false) } }
      instance.start(1000); setRecording(true)
      timer.current = setTimeout(() => { if (instance.state === 'recording') instance.stop() }, 120000)
    } catch (failure) { stopTracks(); if (alive.current) { setError(failure.name === 'NotAllowedError' ? 'Microphone access was denied. Allow it in your browser or attach an audio file.' : failure.message); latest.current.onRecordingChange?.(false) } }
    finally { if (alive.current) setRequesting(false) }
  }
  return <div className="space-y-2 text-xs" onPaste={event => { const images = [...event.clipboardData.files].filter(file => file.type.startsWith('image/')); if (images.length && !disabled && !recording) { event.preventDefault(); add(images) } }}>
    <div className="flex flex-wrap gap-2">
      <label className="rounded border border-border px-2 py-1 cursor-pointer">{imagesOnly ? 'Attach images' : 'Attach image or audio'}<input aria-label={imagesOnly ? 'Attach alert images' : 'Attach note media'} className="sr-only" type="file" multiple accept={(imagesOnly ? IMAGE_TYPES : [...IMAGE_TYPES, ...AUDIO_TYPES]).join(',')} disabled={disabled || recording || requesting} onChange={event => { add([...event.target.files]); event.target.value = '' }}/></label>
      {!imagesOnly && <button type="button" className="rounded border border-border px-2 py-1" disabled={disabled || requesting} onClick={() => recording ? recorder.current?.stop() : record()}>{requesting ? 'Waiting for microphone…' : recording ? 'Stop recording' : 'Record voice note'}</button>}
    </div>
    {recording && <p role="status" className="text-red-500">Recording… stops automatically after 2 minutes.</p>}
    {error && <p role="alert" className="text-red-500">{error}</p>}
    {files.map((file, index) => <div key={index}><div className="flex items-center justify-between gap-2"><span className="truncate">{file.name} ({Math.ceil(file.size / 1024)} KB)</span><button type="button" disabled={disabled} aria-label={`Remove ${file.name}`} onClick={() => onChange(files.filter((_, i) => i !== index))}>Remove</button></div><DraftPreview file={file}/></div>)}
    <p className="text-inkMuted">Up to 5 attachments, 10 MB each.{imagesOnly ? '' : ' Voice notes are saved as audio.'}</p>
  </div>
}
